import { WorkEmpty } from '../work-page.tsx';
import './payment-surfaces.css';
import InvoiceOptionsSection from './invoice-options-section.tsx';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, LoadingBlock, Select, TextInput } from '../ui.tsx';
import {
  emptyTranslationDraft,
  translationDraftFrom,
  translationPayload,
  type TranslationDraft,
} from '../localized-fields.tsx';
import PaymentMethodTranslations from './payment-method-translations.tsx';
import { useLocalizedContent } from '../../i18n/localized.ts';
import {
  PAYMENT_METHOD_FAMILIES,
  SUGGESTED_PAYMENT_METHOD_NAMES,
  paymentMethodAlreadyNamed,
  type PaymentMethodFamily,
} from '@shared/constants/index.ts';

import type { CompanyPaymentMethod } from '@shared/types/index.ts';

/**
 * The company's own list of ways it accepts money — what "Record payment"
 * offers. Independent of the provider connection on purpose: a company that
 * never touches Stripe still takes checks, and this list is what lets it
 * track payment status anyway.
 */
export default function PaymentMethodsSection() {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const queryClient = useQueryClient();
  const canWrite = can('payments:write');
  /* The label everyone reads, in their own language. */
  const localized = useLocalizedContent();

  const [name, setName] = useState('');
  const [nameTranslations, setNameTranslations] = useState<TranslationDraft>(emptyTranslationDraft);
  const [family, setFamily] = useState<PaymentMethodFamily>('other');
  /*
    Which method is being renamed, if any. An inline form rather than the
    `window.prompt` this used to be: a prompt holds one string, and a method
    now has a label per language.
  */
  const [editing, setEditing] = useState<CompanyPaymentMethod | null>(null);
  const [editName, setEditName] = useState('');
  const [editTranslations, setEditTranslations] = useState<TranslationDraft>(emptyTranslationDraft);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  /*
    Which language a method's `name` is written in comes from the ROW; see the
    room-type editor for why it cannot come from the company.

    Two languages, not one, because both forms can be open at once: the rename
    form belongs to the row it is editing, while the add form below belongs to
    whoever is typing into it. Sharing a value had the add form offering a
    Spanish box while its own save called the typed text English.
  */
  const editBaseLanguage = editing?.textLanguage ?? localized.language;
  const addBaseLanguage = localized.language;

  const methods = useQuery({
    queryKey: ['payment-methods', 'all'],
    queryFn: () => paymentApi.listMethods(true),
  });

  function onFailure(caught: unknown) {
    setMessage({
      tone: 'error',
      text: caught instanceof ApiRequestError ? caught.message : t('paymentMethods.actionFailed'),
    });
  }
  function refreshed(text: string) {
    setMessage({ tone: 'success', text });
    // The prefix catches the record-payment forms' active-only query too, so
    // a method added or restored here is offerable immediately.
    void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
  }

  const create = useMutation({
    mutationFn: (input: { name: string; family: PaymentMethodFamily; nameTranslations: TranslationDraft }) =>
      paymentApi.createMethod({
        name: input.name.trim(),
        nameTranslations: translationPayload(input.nameTranslations, addBaseLanguage, input.name.trim()),
        textLanguage: addBaseLanguage,
        family: input.family,
      }),
    onSuccess: (method, input) => {
      queryClient.setQueryData<{ items: CompanyPaymentMethod[] }>(['payment-methods', 'all'], (current) => {
        const existing = current?.items ?? [];
        if (existing.some((row) => row.id === method.id)) return current ?? { items: existing };
        return { items: [...existing, method] };
      });
      // A tap on a suggestion adds that name on its own. A name already
      // sitting in the box stays there, so typing "Gift card" and then
      // tapping Venmo does not throw the typed name away.
      if (name.trim().toLowerCase() === input.name.trim().toLowerCase()) {
        setName('');
        setNameTranslations(emptyTranslationDraft());
        setFamily('other');
      }
      refreshed(t('paymentMethods.added', { name: method.name }));
    },
    onError: onFailure,
  });

  const setActive = useMutation({
    mutationFn: (input: { methodId: string; isActive: boolean }) =>
      paymentApi.updateMethod(input.methodId, { isActive: input.isActive }),
    onSuccess: (method) =>
      refreshed(
        method.isActive
          ? t('paymentMethods.restored', { name: method.name })
          : t('paymentMethods.hidden', { name: method.name }),
      ),
    onError: onFailure,
  });

  const rename = useMutation({
    mutationFn: () =>
      paymentApi.updateMethod(editing?.id ?? '', {
        name: editName.trim(),
        nameTranslations: translationPayload(editTranslations, editBaseLanguage, editName.trim()),
      }),
    onSuccess: (method) => {
      setEditing(null);
      refreshed(t('paymentMethods.renamed', { name: method.name }));
    },
    onError: onFailure,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim().length === 0) return;
    create.mutate({ name, family, nameTranslations });
  }

  function addSuggested(label: string) {
    create.mutate({ name: label, family: 'other', nameTranslations: emptyTranslationDraft() });
  }

  function startRename(method: CompanyPaymentMethod) {
    setEditing(method);
    setEditName(method.name);
    setEditTranslations(translationDraftFrom(method.nameTranslations));
    setMessage(null);
  }

  function submitRename(event: FormEvent) {
    event.preventDefault();
    if (editName.trim().length === 0) return;
    rename.mutate();
  }

  const items = methods.data?.items ?? [];
  const draftMethods =
    name.trim() || Object.values(nameTranslations).some((value) => value.trim())
      ? [{ name: name.trim(), nameTranslations }]
      : [];
  const suggestions = SUGGESTED_PAYMENT_METHOD_NAMES.filter(
    (label) => !paymentMethodAlreadyNamed(items, label) && !paymentMethodAlreadyNamed(draftMethods, label),
  );

  return (
    <section className="card work-section payment-leaf mb-6" aria-labelledby="payment-methods-heading">
      <div className="work-section-heading">
        <h2 id="payment-methods-heading" className="mb-1 text-lg font-semibold">
          {t('paymentMethods.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('paymentMethods.explainer')}</p>

      {message ? (
        <div className="mb-3">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {methods.isLoading ? <LoadingBlock /> : null}
      {methods.isError ? <Alert tone="error">{(methods.error as Error).message}</Alert> : null}
      {items.length > 0 ? (
        <ul className="mb-4 divide-y divide-[color:var(--color-line)] text-sm">
          {items.map((method) => (
            <li key={method.id} className="py-2">
              {editing?.id === method.id ? (
                <form onSubmit={submitRename} className="rounded-md border border-[color:var(--color-line)] p-3">
                  <fieldset disabled={rename.isPending} className="contents">
                    <TextInput
                      label={t('paymentMethods.nameLabel')}
                      value={editName}
                      onChange={(event) => setEditName(event.target.value)}
                      maxLength={40}
                    />
                    <PaymentMethodTranslations
                      baseLanguage={editBaseLanguage}
                      value={editTranslations}
                      onChange={(language, value) =>
                        setEditTranslations((current) => ({ ...current, [language]: value }))
                      }
                    />
                  </fieldset>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      busy={rename.isPending}
                      disabled={editName.trim().length === 0}
                    >
                      {t('paymentMethods.saveName')}
                    </Button>
                    <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={method.isActive ? '' : 'text-[color:var(--color-muted)] line-through'}>
                    {localized.text(method.name, method.nameTranslations)}
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {t(`paymentMethods.family.${method.family}`)}
                    </span>
                  </span>
                  {canWrite ? (
                    <span className="flex gap-2">
                      <Button variant="ghost" disabled={editing !== null} onClick={() => startRename(method)}>
                        {t('paymentMethods.rename')}
                      </Button>
                      <Button
                        variant="ghost"
                        busy={setActive.isPending}
                        disabled={editing !== null}
                        onClick={() => setActive.mutate({ methodId: method.id, isActive: !method.isActive })}
                      >
                        {method.isActive ? t('paymentMethods.hide') : t('paymentMethods.restore')}
                      </Button>
                    </span>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : methods.isSuccess ? (
        <WorkEmpty title={t('paymentMethods.empty')} />
      ) : null}

      {canWrite && methods.isSuccess && suggestions.length > 0 ? (
        <div className="mb-4">
          <p id="payment-method-suggestions-hint" className="mb-2 text-sm text-[color:var(--color-muted)]">
            {t('paymentMethods.suggestionsHint')}
          </p>
          <div role="group" aria-labelledby="payment-method-suggestions-hint" className="flex flex-wrap gap-2">
            {suggestions.map((label) => (
              <Button key={label} variant="secondary" disabled={create.isPending} onClick={() => addSuggested(label)}>
                {label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {canWrite ? (
        <form onSubmit={submit}>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <TextInput
                label={t('paymentMethods.nameLabel')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('paymentMethods.namePlaceholder')}
                maxLength={40}
              />
            </div>
            <Select
              label={t('paymentMethods.familyLabel')}
              value={family}
              onChange={(event) => setFamily(event.target.value as PaymentMethodFamily)}
              options={PAYMENT_METHOD_FAMILIES.map((value) => ({
                value,
                label: t(`paymentMethods.family.${value}`),
              }))}
            />
          </div>
          <PaymentMethodTranslations
            baseLanguage={addBaseLanguage}
            value={nameTranslations}
            onChange={(language, value) => setNameTranslations((current) => ({ ...current, [language]: value }))}
          />
          <Button type="submit" variant="primary" busy={create.isPending} disabled={name.trim().length === 0}>
            {t('paymentMethods.add')}
          </Button>
        </form>
      ) : null}
      <InvoiceOptionsSection />
    </section>
  );
}
